import { Drawer } from '@material-ui/core';
import * as React from 'react';
import { StatusCode } from '../../../../utils/api';
import { AppUtils } from '../../../../utils/app-utils';
import { ShipmentType } from '../../../models/enums/shipment-type';
import { DeliveryShipmentAction } from '../../../models/shipments/shipment-action';
import { TransportationSvc } from '../../../service';
import { ShipmentEditDeliveryAddress } from '../ShipmentEditDeliveryAddress';
import { Address } from "../../../../models/utilities/address";
import { AddressApproveStatus, ApproveAddress } from '../../../models/address/transportation-approve-address';
import ResultDialog from '../../../../components/dialog-types/ResultDialog';
import YesNoCancelDialog from '../../../../components/dialog-types/YesNoCancelDialog';
import { showSuggestedAddressConfirmation } from '../../../../components/dialog-types/SuggestedAddressConfirmation';

interface IProps {
    visible         : boolean;
    onClose         : any;
    onSuccess       : any;
    beforeSave      : any;
    truckId         : number;
    truckVersion    : string;
    stop            : DeliveryShipmentAction|undefined;
    entityType      : ShipmentType;
}

export const ActionEditDeliveryAddress = (props: IProps) => {

    const [service] = React.useState(new TransportationSvc());
    const [editStop, setEditStop] = React.useState<DeliveryShipmentAction>(new DeliveryShipmentAction());

    /**
     * Hydrate the required properties for editing
     */
    React.useEffect(() => {
        if (props.visible && props.stop) {
            const tmpStop = { ...props.stop };
            // tmpStop.isFixedFreight = tmpStop.fixedFreightAmount ? true : false;
            setEditStop(tmpStop);
        }
    }, [props.visible]);

    /**
     * Close handler for the flyout
     */
    const onClose = () => {
        setEditStop({} as unknown as DeliveryShipmentAction);
        props.onClose();
    };

    /**
     * Save the schedule back to the server, and if successful
     * close the flyout
     */
    const onSave = async (addressToUpdate: Address, phoneNumber: string) => {

        props.beforeSave();

        let requestAddress = { ...addressToUpdate };

        if (props.stop) {
            const result = await service.updateAddress(
                requestAddress,
                phoneNumber,
                props.truckId,
                props.stop.id,
                props.truckVersion,
                props.entityType
            );

            if (result.status === StatusCode.Success) {
                if (result.data?.status === AddressApproveStatus.Success) {
                    onSaveSuccess();
                }
                else if (result.data.status === AddressApproveStatus.NotApprovedGoogleHasSuggestion) {
                    const confirmationResult = await showSuggestedAddressConfirmation(addressToUpdate, result.data.suggestedAddress);

                    if (confirmationResult === true) {
                        requestAddress.addressLine1 = result.data.suggestedAddress.addressLine1 ?? '';
                        requestAddress.addressLine2 = result.data.suggestedAddress.addressLine2 ?? '';
                        requestAddress.city = result.data.suggestedAddress.city ?? '';
                        requestAddress.stateProvince = result.data.suggestedAddress.stateProvince ?? '';
                        requestAddress.postalCode = result.data.suggestedAddress.postalCode ?? '';
                        requestAddress.country = result.data.suggestedAddress.country || '';
                        requestAddress.latitude = result.data.suggestedAddress.latitude ?? 0;
                        requestAddress.longitude = result.data.suggestedAddress.longitude ?? 0;
                        addressToUpdate.timeZoneId = result.data.suggestedAddress.timeZoneId;
                        const forceResult = await service.updateAddress(
                            requestAddress,
                            phoneNumber,
                            props.truckId,
                            props.stop.id,
                            props.truckVersion,
                            props.entityType,
                            false
                        );
                        if (forceResult.status === StatusCode.Success) {
                            if (forceResult.data?.status === AddressApproveStatus.Success) {
                                onSaveSuccess();
                            }
                        }
                    }
                    else if (confirmationResult === false) {
                        const forceResult = await service.updateAddress(
                            requestAddress,
                            phoneNumber,
                            props.truckId,
                            props.stop.id,
                            props.truckVersion,
                            props.entityType,
                            true
                        );

                        if (forceResult.status === StatusCode.Success) {
                            if (forceResult.data?.status === AddressApproveStatus.Success) {
                                onSaveSuccess();
                            }
                        }
                    }
                    // else they cancelled
                }
                else if (result.data.status === AddressApproveStatus.NotApprovedGoogleNotFound) {
                    const confirmationResult = await showAddressConfirmation(addressToUpdate);
                    if (confirmationResult === true) {
                    const forceResult = await service.updateAddress(
                        requestAddress,
                        phoneNumber,
                        props.truckId,
                        props.stop.id,
                        props.truckVersion,
                        props.entityType,
                        true
                    );

                    if (forceResult.status === StatusCode.Success) {
                        if (forceResult.data?.status === AddressApproveStatus.Success) {
                            onSaveSuccess();
                        }
                    }
                }
                }
                else {
                    // some other error
                }
            }
        }
    };

    /**
 * Prompt the user to confirm whether to use the provided address even if google could not find a matching address.
 * @param address The original entered address
 */
    const showAddressConfirmation = async (addressVM: Address) => {

        const message = (
            <>
                <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 20 }}>
                    Google could not find a matching address. Do you want to continue using the address you entered?
                </div>

                <div style={{ textAlign: 'left' }}>
                    <strong>Address:</strong>
                    {formatAddress(addressVM)}
                </div>
            </>
        );

        const result = await ResultDialog.show({
            title: 'Address Confirmation',
            messageContent: message,
            affirmativeLabel: 'Yes',
            negativeLabel: 'Cancel'
        });

        return result;
    };

    const formatAddress = (addr: ApproveAddress) => (
        <div>
            <div>{addr.addressLine1}</div>
            {addr.addressLine2 && <div>{addr.addressLine2}</div>}
            <div>
                {addr.city}{addr.city && addr.stateProvince ? ', ' : ''}{addr.stateProvince} {addr.postalCode}
            </div>
            <div>{addr.country}</div>
            <div>Latitude: {addr.latitude}</div>
            <div>Longitude: {addr.longitude}</div>
            <div>TimeZoneId: {addr.timeZoneId}</div>
        </div>
    );

    function onSaveSuccess() {
        setEditStop({} as unknown as DeliveryShipmentAction);
        props.onSuccess();
    }

    return <Drawer
        anchor="right"
        onClose={onClose}
        open={props.visible}>
        {
            !AppUtils.IsEmpty(editStop)
                ? <div className="stop-action-drawer stop-action-edit-delivery-address">
                    <ShipmentEditDeliveryAddress
                        stop={editStop}
                        onSave={onSave}
                        onCancel={onClose}
                        visible={props.visible}
                        truckId={props.truckId}
                        truckVersion={props.truckVersion}
                    />
                </div>
                : null
        }
    </Drawer>;
};
